import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import {
  View,
  StatusBar,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView
} from 'react-native'

import { SafeAreaView, SafeAreaProvider } from 'react-native-safe-area-context'

import {
  Images,
  Colors,
  useTheme,
  withTheme
} from '@vfgroup-oneplatform/foundation/Components/Themes'
import colors from '@vfgroup-oneplatform/foundation/Components/Themes/Colors'

import {
  Icon,
  VFText,
  VFButton,
  QuickActionMenu
} from '@vfgroup-oneplatform/foundation/Components'
import LinearGradient from 'react-native-linear-gradient'

import { STATUS } from '../../Utils/members'

// Components
import Offer from '../../Components/Offer'
import MembersList from '../../Components/MembersList'
import VFTextWithLoader from '../../Components/VFTextWithLoader'
import CustomInput from '../../Components/CustomInput/CustomInput'
import CustomErrorScreen from '../../Components/CustomErrorScreen'
import LeaveGroupWarning from '../../Components/LeaveGroupWarning'

import { UhImages, getThemeImages } from '../../Themes'

import styles from './GroupScreen.Styles'

const defaultState = {
  data: null,
  isError: false,
  isLoading: false,
  isSuccess: false
}

const GroupScreen = ({
  addMember,
  leaveGroup,
  removeMember,
  nameValidator,
  loggedInNumber,
  getGroupMembers,
  numberFormatter,
  numberValidator,
  getGroupContract,
  navigateToScreen
}) => {
  const [showNameEdit, setShowNameEdit] = useState(false)
  const [showLeaveWarning, setShowLeaveWarning] = useState(false)

  // Group Offer
  const [groupOffer, setGroupOffer] = useState(defaultState)

  // Group Members
  const [groupData, setGroupData] = useState(defaultState)

  const theme = useTheme()

  const handleNameEdit = () => setShowNameEdit(true)

  const getMembers = () => {
    getGroupMembers({
      onStart: () => {
        setGroupData(prev => {
          return {
            ...prev,
            data: null,
            isError: false,
            isLoading: true,
            errorMessage: ''
          }
        })
      },
      onSuccess: data => {
        setGroupData(prev => {
          return {
            ...prev,
            data: data,
            isError: false,
            isLoading: false,
            errorMessage: ''
          }
        })
      },
      onError: err => {
        setGroupData(prev => {
          return {
            ...prev,
            data: null,
            isError: true,
            isLoading: false,
            errorMessage: err.message
          }
        })
      }
    })
  }

  const getOffer = () => {
    getGroupContract({
      onStart: () => {
        setGroupOffer(prev => {
          return {
            ...prev,
            data: null,
            isError: false,
            isLoading: true,
            errorMessage: ''
          }
        })
      },
      onSuccess: data => {
        setGroupOffer(prev => {
          return {
            ...prev,
            data: data,
            isError: false,
            isLoading: false,
            errorMessage: ''
          }
        })
      },
      onError: err => {
        setGroupOffer(prev => {
          return {
            ...prev,
            data: null,
            isError: true,
            isLoading: false,
            errorMessage: err.message
          }
        })
      }
    })
  }

  const initialFetch = async () => Promise.all([getMembers(), getOffer()])

  useEffect(() => {
    initialFetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLeaveConfirm = () => {
    setShowLeaveWarning(false)
    leaveGroup({
      onStart: () => {},
      onSuccess: () => {},
      onError: () => {}
    })
  }

  const handleAdd = number => {
    addMember({
      body: {
        number,
        admin: groupData.data?.admin,
        tariffPlan: groupOffer.data?.offer?.tariffPlan?.value
      },
      onStart: () => {
        setGroupData(prev => {
          return {
            ...prev,
            data: {
              ...(prev.data ?? {}),
              members: [
                ...(prev.data?.members ?? []),
                { number, role: 'member', status: STATUS.loading }
              ]
            }
          }
        })
      },
      onSuccess: data => {
        setGroupData(prev => {
          return {
            ...prev,
            data: {
              ...(prev.data ?? {}),
              members: (prev.data.members ?? []).map(member => {
                if (member?.number === data?.number) {
                  return {
                    ...member,
                    status: STATUS.waiting_confirmation
                  }
                }
                return member
              })
            }
          }
        })
      },
      onError: error => {
        setGroupData(prev => {
          return {
            ...prev,
            data: {
              ...(prev.data ?? {}),
              members: prev.data.members?.map(member => {
                if (member.number === error.number) {
                  return {
                    ...member,
                    status: STATUS.error,
                    error: error.message
                  }
                }
                return member
              })
            }
          }
        })
      }
    })
  }

  const handleRemove = number => {
    const memberToRemove = groupData?.data?.members.find(
      member => member.number === number
    )
    if (!memberToRemove) {
      return
    }
    // If it failed to add just remove it locally
    if (memberToRemove.status === STATUS.error) {
      setGroupData(prev => {
        return {
          ...prev,
          data: {
            ...(prev.data ?? {}),
            members: prev.data?.members?.filter(
              member => member.number !== number
            )
          }
        }
      })
      return
    }
    removeMember({
      body: { number },
      onStart: () => {
        setGroupData(prev => {
          return {
            ...prev,
            data: {
              ...(prev.data ?? {}),
              members: prev.data?.members?.map(member => {
                if (member.number === number) {
                  return {
                    ...member,
                    status: STATUS.loading
                  }
                } else {
                  return { ...member }
                }
              })
            }
          }
        })
      },
      onSuccess: () => {
        setGroupData(prev => {
          return {
            ...prev,
            data: {
              ...(prev.data ?? {}),
              members: prev.data?.members?.map(member => {
                if (member.number === number) {
                  return {
                    ...member,
                    status: STATUS.marked_for_leave
                  }
                } else {
                  return { ...member }
                }
              })
            }
          }
        })
      },
      onError: error => {
        setGroupData(prev => {
          return {
            ...prev,
            data: {
              ...(prev.data ?? {}),
              members: prev.data?.members?.map(member => {
                if (member.number === number) {
                  return {
                    ...member,
                    error: error,
                    status: STATUS.error
                  }
                } else {
                  return { ...member }
                }
              })
            }
          }
        })
      }
    })
  }

  const handleRenameConfirm = () => {}

  // const showEdit = !groupOffer?.isLoading && !groupOffer?.isError

  return (
    <SafeAreaProvider>
      <StatusBar barStyle="light-content" />
      {/* Header */}
      <GroupHeader
        // showEdit={showEdit}
        onEdit={handleNameEdit}
        isLoading={groupOffer.isLoading}
        groupName={groupOffer.data?.name ?? ''}
        onClose={() => navigateToScreen('Dashboard')}
      />
      <View style={styles.flex}>
        <View style={[styles.scroll(theme), styles.flex]}>
          <ScrollView style={[styles.flex, styles.padding]}>
            <View style={[styles.paddingBottom(4), styles.flex]}>
              {/* Admin group management */}
              <>
                <View style={[styles.flexRow, styles.paddingTop(8)]}>
                  <VFText
                    i18nKey="unlimited_my_members"
                    style={[styles.title]}
                  />
                </View>
                <VFText
                  style={[styles.subtitle, styles.paddingTop(4)]}
                  i18nKey="unlimited_member_description"
                />
                <View style={styles.paddingTop(4)}>
                  {groupData.isError ? (
                    <View style={styles.flex}>
                      <CustomErrorScreen onPress={getMembers} />
                    </View>
                  ) : (
                    <MembersList
                      hideInputOnFill
                      showHeaderCount
                      onAdd={handleAdd}
                      onRetry={getMembers}
                      onRemove={handleRemove}
                      loggedInNumber={loggedInNumber}
                      isLoading={groupData.isLoading}
                      numberFormatter={numberFormatter}
                      numberValidator={numberValidator}
                      isGroupAdmin={groupData.data?.isAdmin}
                      members={groupData.data?.members ?? []}
                      groupCount={groupOffer.data?.offer?.slots?.value}
                    />
                  )}
                </View>
              </>
              {/* Group offer */}
              <>
                <VFText
                  i18nKey="unlimited_group_plan"
                  style={[styles.title, styles.paddingTop(8)]}
                />
                <VFText
                  style={[styles.subtitle, styles.paddingTop(4)]}
                  i18nKey="unlimited_family_offer"
                />
                <View style={[styles.paddingTop(4), styles.paddingBottom(4)]}>
                  {groupOffer.isError ? (
                    <View style={styles.flex}>
                      <CustomErrorScreen onPress={getOffer} />
                    </View>
                  ) : (
                    <Offer
                      viewOnly
                      type="bundle"
                      stylesArray={[styles.card(theme)]}
                      isLoading={groupOffer.isLoading}
                      offer={groupOffer.data?.offer ?? {}}
                    />
                  )}
                </View>
              </>
              {/* Group member leave button */}
              {/* {!isGroupAdmin && (
              <View style={[styles.paddingTop(12)]}>
                <VFButton
                  onPress={handleLeavePress}
                  disabled={!isEligibleToLeave}
                  title="unlimited_leave_group_button"
                />
                {!isEligibleToLeave && (
                  <View style={[styles.paddingTop(6), styles.leaveExplain]}>
                    <VFText i18nKey="unlimited_leave_group_explain" />
                    <VFText i18nKey=" 27.12.2022." style={styles.leaveDate} />
                  </View>
                )}
              </View>
            )} */}
              <LeaveGroupWarning
                show={showLeaveWarning}
                onConfirm={handleLeaveConfirm}
                onClose={() => setShowLeaveWarning(false)}
              />
              <EditNameModal
                show={showNameEdit}
                nameValidator={nameValidator}
                onConfirm={handleRenameConfirm}
                onClose={() => setShowNameEdit(false)}
              />
            </View>
          </ScrollView>
        </View>
      </View>
    </SafeAreaProvider>
  )
}

const GroupHeader = ({ onEdit, showEdit, groupName, isLoading, onClose }) => {
  const handleNameEdit = () => {
    if (showEdit) {
      onEdit()
    }
  }

  return (
    <LinearGradient
      angle={30}
      useAngle={true}
      angleCenter={{ x: 0, y: 0 }}
      colors={[Colors.darkRedTwo, Colors.vfRed]}
    >
      <SafeAreaView edges={['top']}>
        <View style={styles.headerContainer}>
          {/* Title */}
          <View style={styles.headerTitle}>
            <View />
            <VFText
              style={[styles.white, styles.headerText]}
              i18nKey="unlimited_my_offer"
            />
            <TouchableOpacity
              onPress={onClose}
              activeOpacity={0.8}
              style={styles.headerIcon}
            >
              <Icon name={Images.ic_close_white} size={25} type="image" />
            </TouchableOpacity>
          </View>
          <View style={styles.headerContent}>
            {/* Group Name */}
            <View style={styles.paddingBottom(3)}>
              <VFText i18nKey="unlimited_my_members" style={styles.white} />
              <View style={styles.subHeader}>
                <View>
                  <VFTextWithLoader
                    loaderWidth={100}
                    loaderHeight={24}
                    isLoading={isLoading}
                    background={colors.vfRed}
                    i18nKey="%1$s"
                    placeHolders={[groupName]}
                    foreground={colors.activeRed}
                    style={[styles.white, styles.headerDescription]}
                  />
                </View>
                <View>
                  {showEdit ? (
                    <TouchableOpacity
                      activeOpacity={0.8}
                      onPress={handleNameEdit}
                    >
                      <Icon name={UhImages.edit} size={20} type="image" />
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>
            </View>
            {/* Home Icon */}
            <Icon name={UhImages.group} size={145} type="image" />
          </View>
        </View>
      </SafeAreaView>
    </LinearGradient>
  )
}

GroupHeader.propTypes = {
  onEdit: PropTypes.func,
  onClose: PropTypes.func,
  showEdit: PropTypes.bool,
  isLoading: PropTypes.bool,
  groupName: PropTypes.string
}

const errorDefaults = {
  isError: false,
  message: ''
}

const EditNameModal = ({ show, onConfirm, onClose, nameValidator }) => {
  const [name, setName] = useState('')
  const [error, setError] = useState(errorDefaults)

  const theme = useTheme()
  const themedImages = getThemeImages(theme.name)

  const validate = value => {
    const data = nameValidator(value)
    setError(data)
    return data
  }

  const handleConfirm = () => {
    const data = validate(name)
    !data.isError && onConfirm(name)
  }

  return (
    <QuickActionMenu
      type="secondary"
      isVisible={show}
      toggleModal={onClose}
      title="unlimited_rename_group_title"
    >
      <KeyboardAvoidingView keyboardVerticalOffset={100}>
        <View style={styles.nameModalContent}>
          <View style={styles.nameModalText}>
            <VFText
              i18nKey="unlimited_rename_group_header"
              style={styles.nameHeader}
            />
            <VFText
              i18nKey="unlimited_rename_group_desc"
              style={styles.nameDesc}
            />
          </View>
          <View style={styles.nameForm}>
            <View style={styles.formHeader}>
              <Icon
                size={32}
                type="image"
                style={styles.icon}
                name={themedImages.members}
              />
              <View>
                <VFText
                  i18nKey="unlimited_id_name_label"
                  style={styles.nameLabel}
                />
                <VFText i18nKey="Lorem ipsum" style={styles.nameSubLabel} />
              </View>
            </View>
            <View style={styles.divider} />
            <View>
              <CustomInput
                value={name}
                validator={validate}
                onChangeText={setName}
                isError={error.isError}
                errorMessage={error.message}
                label="unlimited_group_name_label"
                resetValidation={() => setError(errorDefaults)}
              />
            </View>
          </View>
        </View>
        <View>
          <VFButton
            onPress={handleConfirm}
            style={styles.continueBtn}
            title="unlimited_continue"
          />
          <VFButton type="secondary" onPress={onClose} title="unlimited_back" />
        </View>
      </KeyboardAvoidingView>
    </QuickActionMenu>
  )
}

EditNameModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  nameValidator: PropTypes.func
}

GroupScreen.propTypes = {
  addMember: PropTypes.func,
  leaveGroup: PropTypes.func,
  removeMember: PropTypes.func,
  nameValidator: PropTypes.func,
  getGroupMembers: PropTypes.func,
  numberFormatter: PropTypes.func,
  numberValidator: PropTypes.func,
  loggedInNumber: PropTypes.string,
  getGroupContract: PropTypes.func,
  navigateToScreen: PropTypes.func
}

export default withTheme(GroupScreen)
